export type GlobalValues =
  'inherit' | 'initial' | 'revert' | 'revert-layer' | 'unset'

export enum FontWeight {
  Thin = 100,
  ExtraLight = 200,
  Light = 300,
  Normal = 400,
  Medium = 500,
  SemiBold = 600,
  Bold = 700,
  ExtraBold = 800,
  Black = 900,
  ExtraBlack = 950,
}

export interface TextStyle {
  fontFamily: string
  fontSize: string | string[]
  fontWeight:
    'normal' | 'bold' | 'lighter' | 'bolder' | GlobalValues | FontWeight
  lineHeight?: number | string | 'normal' | GlobalValues
  letterSpacing?: string
  textTransform?:
    | 'none'
    | 'capitalize'
    | 'uppercase'
    | 'lowercase'
    | 'full-width'
    | GlobalValues
}

// default = Body1
// superLarge = Display2
// exLarge = H1
// large = H3
// mediumBold = H5
// medium = H6
// normal = Body1
// buttonBold = Body2_Bold
// button = Body2
// bSmall = Body2_Semibold
// small = Body2
// bTinySmall = Body3
// btiny = Body4_Bold
// tiny = Body4
// exTiny = Body4_Bold
// superTiny = Body5_ExtraBold
export interface TextStyles {
  Display1: TextStyle
  Display2: TextStyle
  H1: TextStyle
  H2: TextStyle
  H3: TextStyle
  H4: TextStyle
  H5: TextStyle
  H6: TextStyle
  Body1: TextStyle
  Body1_Bold: TextStyle
  Body2: TextStyle
  Body2_Bold: TextStyle
  Body2_Semibold: TextStyle
  Body3: TextStyle
  Body3_Bold: TextStyle
  Body4: TextStyle
  Body4_Bold: TextStyle
  Body5_ExtraBold: TextStyle
}

const fontFamilyInter = "'Inter', Helvetica, sans-serif"

export const textStyles: Record<string, TextStyle> & TextStyles = {
  Display1: {
    fontFamily: fontFamilyInter,
    fontSize: '80px',
    fontWeight: FontWeight.SemiBold,
    letterSpacing: '-0.8px',
    lineHeight: '84px',
  },
  Display2: {
    fontFamily: fontFamilyInter,
    fontSize: '56px',
    fontWeight: FontWeight.Bold,
    letterSpacing: '-0.56px',
    lineHeight: '64px',
  },
  H1: {
    fontFamily: fontFamilyInter,
    fontSize: '32px',
    fontWeight: FontWeight.ExtraBold,
    letterSpacing: '-0.32px',
    lineHeight: '40px',
  },
  H2: {
    fontFamily: fontFamilyInter,
    fontSize: '32px',
    fontWeight: FontWeight.Normal,
    lineHeight: '40px',
  },
  H3: {
    fontFamily: fontFamilyInter,
    fontSize: '24px',
    fontWeight: FontWeight.Bold,
    lineHeight: '32px',
  },
  H4: {
    fontFamily: fontFamilyInter,
    fontSize: '20px',
    fontWeight: FontWeight.ExtraBold,
    lineHeight: '28px',
  },
  H5: {
    fontFamily: fontFamilyInter,
    fontSize: '20px',
    fontWeight: FontWeight.Bold,
    lineHeight: '28px',
  },
  H6: {
    fontFamily: fontFamilyInter,
    fontSize: '20px',
    fontWeight: FontWeight.Normal,
    lineHeight: '28px',
  },
  Body1: {
    fontFamily: fontFamilyInter,
    fontSize: '16px',
    fontWeight: FontWeight.Normal,
    lineHeight: '24px',
  },
  Body1_Bold: {
    fontFamily: fontFamilyInter,
    fontSize: '16px',
    fontWeight: FontWeight.Bold,
    lineHeight: '24px',
  },
  Body2: {
    fontFamily: fontFamilyInter,
    fontSize: '14px',
    fontWeight: FontWeight.Normal,
    lineHeight: '20px',
  },
  Body2_Bold: {
    fontFamily: fontFamilyInter,
    fontSize: '14px',
    fontWeight: FontWeight.Bold,
    lineHeight: '20px',
  },
  Body2_Semibold: {
    fontFamily: fontFamilyInter,
    fontSize: '14px',
    fontWeight: FontWeight.SemiBold,
    lineHeight: '20px',
  },
  Body3: {
    fontFamily: fontFamilyInter,
    fontSize: '12px',
    fontWeight: FontWeight.Normal,
    lineHeight: '16px',
  },
  Body3_Bold: {
    fontFamily: fontFamilyInter,
    fontSize: '12px',
    fontWeight: FontWeight.Bold,
    lineHeight: '16px',
  },
  Body4: {
    fontFamily: fontFamilyInter,
    fontSize: '11px',
    fontWeight: FontWeight.Normal,
    letterSpacing: '-0.11px',
    lineHeight: '16px',
  },
  Body4_Bold: {
    fontFamily: fontFamilyInter,
    fontSize: '11px',
    fontWeight: FontWeight.Bold,
    letterSpacing: '-0.11px',
    lineHeight: '16px',
  },
  Body5_ExtraBold: {
    fontFamily: fontFamilyInter,
    fontSize: '8px',
    fontWeight: FontWeight.ExtraBold,
    letterSpacing: '0.03em',
  },
}
